import { create } from 'zustand'

interface BrandingState {
  companyName: string
  companyNameShort: string
  accentColor: string
  primaryColor: string
  sidebarColor: string
  fontBody: string
  fontMono: string
  logoFull: string | null
  logoIcon: string | null
  footerText: string
  features: {
    floorPlans: boolean
    siteDocs: boolean
    timeTracking: boolean
    purchaseOrders: boolean
    deals: boolean
    aiBuilder: boolean
  }
  marginAlertThreshold: number
  marginHighlightEnabled: boolean
  marginProposalWarningEnabled: boolean
  marginBlockBelowZero: boolean
  loaded: boolean
  loadBranding: () => Promise<void>
}

export const useBrandingStore = create<BrandingState>((set) => ({
  companyName: 'Portal',
  companyNameShort: 'Portal',
  accentColor: '#c8850a',
  primaryColor: '#2563eb',
  sidebarColor: '#1c1e24',
  fontBody: 'DM Sans',
  fontMono: 'JetBrains Mono',
  logoFull: null,
  logoIcon: null,
  footerText: '',
  features: {
    floorPlans: true,
    siteDocs: true,
    timeTracking: true,
    purchaseOrders: true,
    deals: true,
    aiBuilder: false,
  },
  marginAlertThreshold: 20,
  marginHighlightEnabled: true,
  marginProposalWarningEnabled: true,
  marginBlockBelowZero: false,
  loaded: false,
  loadBranding: async () => {
    try {
      const res = await fetch('/api/branding', { credentials: 'include' })
      if (res.ok) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data: any = await res.json()
        set({
          companyName: data.companyName || 'Portal',
          companyNameShort: data.companyNameShort || data.companyName?.split(' ')[0] || 'Portal',
          accentColor: data.accentColor || '#c8850a',
          primaryColor: data.primaryColor || '#2563eb',
          sidebarColor: data.sidebarColor || '#1c1e24',
          fontBody: data.fontBody || 'DM Sans',
          fontMono: data.fontMono || 'JetBrains Mono',
          logoFull: data.logoFull || null,
          logoIcon: data.logoIcon || null,
          footerText: data.footerText || '',
          features: data.features || {},
          marginAlertThreshold: data.marginAlertThreshold ?? 20,
          marginHighlightEnabled: data.marginHighlightEnabled !== false,
          marginProposalWarningEnabled: data.marginProposalWarningEnabled !== false,
          marginBlockBelowZero: !!data.marginBlockBelowZero,
          loaded: true,
        })

        // Apply CSS variables
        const root = document.documentElement
        root.style.setProperty('--accent', data.accentColor || '#c8850a')
        root.style.setProperty('--accent-hover', darkenColor(data.accentColor || '#c8850a', 15))
        root.style.setProperty('--accent-soft', `${data.accentColor || '#c8850a'}14`)
        root.style.setProperty('--accent-text', data.accentColor || '#c8850a')
        root.style.setProperty('--primary', data.primaryColor || '#2563eb')
        root.style.setProperty('--sidebar-bg', data.sidebarColor || '#1c1e24')
      }
    } catch (e) {
      console.error('Failed to load branding:', e)
      set({ loaded: true }) // Use defaults on failure
    }
  },
}))

function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent))
  const g = Math.max(0, ((num >> 8) & 0x00FF) - Math.round(2.55 * percent))
  const b = Math.max(0, (num & 0x0000FF) - Math.round(2.55 * percent))
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`
}
